/**
 * TNBEWEB-2019 — the ONE default root-flex object for a web screen.
 *
 * The root level of a screen is laid out by different code in the editor (the
 * synthetic `__flex_root__` wrapper on the canvas) and here (`.pageWrap`). When
 * `settings.style` carries a COMPLETE `flex` object both sides agree, because
 * they map `direction`/`justify_content`/`align_items` through equivalent style
 * helpers. They only diverged when `flex` was ABSENT and each side fell back to
 * something different: the canvas took the browser defaults (`row` /
 * `flex-start` / `stretch`) while `.pageWrap` hardcoded `justify-content:
 * center`, so the same screen sat left on the canvas and centered under the
 * published link. That `center` is gone; this object replaces it.
 *
 * The values are the ones the editor's Screen settings → Layout panel already
 * displays for an untouched screen, so what the panel claims is what renders.
 *
 * ⚠️ Mirrored in `fractal-fe/src/features/tabs/utils/rootFlexDefaults.ts`. The
 * two repos cannot share code, so ANY change here must land in both — a
 * divergence silently reintroduces exactly the bug this module exists to close.
 */

export type RootFlexDirection = 'row' | 'column' | 'row-reverse' | 'column-reverse';
export type RootFlexJustify = 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly';
export type RootFlexAlign = 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch';
export type RootFlexWrap = 'nowrap' | 'wrap' | 'wrap-reverse';

export interface RootFlexStyle {
  direction?: RootFlexDirection;
  justify_content?: RootFlexJustify;
  align_items?: RootFlexAlign;
  gap?: number | string;
  wrap?: RootFlexWrap;
}

export const ROOT_FLEX_DEFAULTS: RootFlexStyle = {
  direction: 'row',
  justify_content: 'flex-start',
  // NOT the browser's `stretch`: with `stretch` a root section that declares no
  // height is measured against whatever box encloses it — the design frame in
  // the editor, the browser window here — so the same screen rendered at a
  // different height on every window size (`.pageWrap` is `100dvh`).
  // `flex-start` makes the section shrink-wrap its content in both places,
  // which is the only height the canvas and the browser can agree on.
  align_items: 'flex-start',
  gap: 0,
  wrap: 'nowrap',
};

/**
 * Fill in every field the saved `flex` object leaves out. `null`/`undefined`
 * entries count as "not set" — a partially-written object (older saves, a
 * legacy `__flex_root__` wrapper style, hand-edited JSON) must not leak a
 * browser default through the gap.
 */
export function withRootFlexDefaults(flex?: RootFlexStyle | null): RootFlexStyle {
  if (!flex || typeof flex !== 'object') return { ...ROOT_FLEX_DEFAULTS };
  const declared = Object.keys(flex).reduce((acc, key) => {
    const value = (flex as Record<string, unknown>)[key];
    if (value !== undefined && value !== null) acc[key] = value;
    return acc;
  }, {} as Record<string, unknown>);
  return { ...ROOT_FLEX_DEFAULTS, ...declared } as RootFlexStyle;
}

export const isColumnRootDirection = (flex?: RootFlexStyle | null): boolean => {
  const { direction } = withRootFlexDefaults(flex);
  return direction === 'column' || direction === 'column-reverse';
};
